<template>
  <div>
    <!-- Work Experience Toggle - always show when applicant has experiences -->
    <div v-if="applicantProfile && applicantProfile.experiences && applicantProfile.experiences.length" class="row justify-content-center">
        <div class="col-12">
            <h2>{{ $t('general.experience') }}</h2>
            <div class="custom-control custom-switch d-flex align-center">
                <input id="detailed-experience-switch"
                    name="detailed-experience-switch"
                    type="checkbox"
                    class="custom-control-input"
                    v-model="showDetailedExperience"
                    @input="$emit('input', $event.target.checked)">
                <label for="detailed-experience-switch" class="custom-control-label d-inline-block"></label>
                <p class="ml-1">{{ showDetailedExperience ? $t('job-offer.hide-detailed-experience') : $t('job-offer.show-detailed-experience') }} <span>({{ applicantProfile.experiences.length }} {{ $t('general.entries') }})</span></p>
            </div>
        </div>
    </div>
    <!-- Detailed Experience Cards -->
    <div v-if="showDetailedExperience && applicantProfile" class="row justify-content-center">
        <div class="col-md-12">
            <transition name="slide-down">
                <div v-if="applicantProfile.experiences && applicantProfile.experiences.length" class="row d-block d-sm-flex px-2 mb-4">
                    <ExperienceCard v-for="(experience, index) in applicantProfile.experiences" :key="'experience-' + index" :experience="experience"></ExperienceCard>
                </div>
            </transition>
        </div>
    </div>
    <!-- Requirements Table - only show when job offer has meaningful requirements -->
    <div v-if="shouldShowRequirementTable">
      <div class="row justify-content-center">
          <div class="col-12">
              <h2>{{ $t('requirements.title') }}</h2>
              <div v-if="applicantProfile && (!applicantProfile.experiences || !applicantProfile.experiences.length)">
                  <GeneralPrompt class="mb-4" :text="$t('prompt.missing-experience', {experience: $t('requirements.title'), applicantProfile: $t('general.applicant-profile')})"></GeneralPrompt>
              </div>
          </div>
      </div>
      <div class="row justify-content-center">
          <div class="col-md-12">
              <div class="mobile-overflow-table mb-4">
                  <table class="table table-striped mb-0">
                      <thead class="thead-dark">
                          <tr>
                              <th>{{ $t('experience.domain')}}</th>
                              <th>{{ $t('experience.expertise') }}</th>
                              <th class="w-33">
                                  {{ $t('experience.required-experience') }}
                              </th>
                              <th class="w-33" v-if="applicantProfile">
                                  {{ $t('general.experience')}} {{ $t('general.applicant-profile') }}
                              </th>
                          </tr>
                      </thead>
                      <tbody v-if="filteredRequirements.length">
                          <tr v-for="(requirement, index) in filteredRequirements" :key="'requirement' + index">
                              <th class="align-top">{{ $t('job-offer.' + requirement.name) }}</th>
                              <!-- <td>{{ jobOffer.requirements.find(r => r.requirementable_type === requirement.name) }}</td> -->
                              <td class="align-top">
                                  <ul class="mb-0 pl-0 list-unstyled">
                                      <li v-if="applicantProfile || exp.jobYears > 0" v-for="(exp, index) in requirement.experiences" :key="index" class="mb-1">
                                          <span v-if="requirement.name === 'jobPositions'">
                                              {{ $t('jobPositions.' + exp.name) }}
                                          </span>
                                          <span v-else-if="requirement.name === 'companySizes'">
                                              {{ $t('company-sizes.' + exp.name) }}
                                          </span>
                                          <span v-else>
                                              {{ exp[$getLocaleDbColumn('name')] || exp.name || exp.name_en || '-' }}
                                          </span>
                                      </li>
                                  </ul>
                              </td>
                              <td class="align-top">
                                  <span v-if="!requirement.experiences"></span>
                                  <ul v-else class="mb-0 pl-0 list-unstyled">
                                      <li v-if="exp.jobYears > 0" v-for="exp in requirement.experiences" :key="exp.id" class="mb-1">
                                          <span v-if="applicantProfile" class="d-inline-block text-center" :class="newGetRequirementMatch(requirement.name, exp.requirementable_id)" style="min-width: 1rem;">
                                              <o-icon :icon="['far', newGetRequirementMatch(requirement.name, exp.requirementable_id) === 'matched' ? 'check' : 'times']" />
                                          </span>
                                          {{ exp.jobYears }} {{ exp.jobYears === 1 ? $t('experience.year') : $t('experience.years') }}
                                      </li>
                                      <li v-else-if="applicantProfile" class="mb-1"><span class="d-inline-block text-center" style="min-width: 1rem;">-</span></li>
                                  </ul>
                              </td>
                              <td v-if="applicantProfile" class="align-top">
                                  <ul class="mb-0 pl-0">
                                      <li v-if="exp.applicantYears > 0" v-for="(exp, index) in requirement.experiences" :key="'applicantExp' + index" class="mb-1">
                                          {{ exp.applicantYears }} {{ exp.applicantYears === 1 ? $t('experience.year') : $t('experience.years') }}
                                      </li>
                                      <li v-else class="mb-1 list-unstyled">-</li>
                                  </ul>
                              </td>
                          </tr>
                      </tbody>
                  </table>
              </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExperienceCard from '~/components/ExperienceCard'
import GeneralPrompt from '~/components/ui/prompts/GeneralPrompt'
import Icon from '~/components/ui/common/Icon'

export default {
    components: {
        ExperienceCard,
        GeneralPrompt,
    },
    props: [
        'jobOffer',
        'match',
        'applicantProfile',
        'hideEmptyJobOfferTables',
    ],
    computed: {
        shouldShowRequirementTable() {
            if (this.hideEmptyJobOfferTables) return this.hasRequirementRows
            return !!(
                this.jobOffer &&
                this.jobOffer.requirements &&
                this.jobOffer.requirements.length > 0
            )
        },
        hasRequirementRows() {
            return this.filteredRequirements.length > 0
        },
        sortedAggregation() {
            const sorted = this.aggregatedExperiences.map(domain => {
                const sortedExperience = domain.experiences.sort((a,b) => b.jobYears - a.jobYears);
                return {
                    ...domain,
                    experiences: sortedExperience,
                };
            });
            return sorted;
        },
        newAggregation() {
            // Return empty array if no job offer or no requirements
            if (!this.jobOffer || !this.jobOffer.requirements) {
                return [];
            }
            return this.blankAggregatedExperiences.map(domain => {
                const filtered = this.jobOffer.requirements.filter(req => {
                    return (req.requirementable_type === domain.name) && (req.years > 0)
                });
                const experiences = filtered.map( req => {
                    if (domain.name === req.requirementable_type) {
                        if ( !this.applicantProfile ) {
                            return {
                                requirementable_id: req.requirementable_id,
                                requirements_id: req.id,
                                jobYears: req.years,
                                name: req.name,
                                name_en: req.name_en,
                            }
                        }
                        const filteredExperiences = this.applicantProfile.experiences.filter(exp => exp[domain.table] === req.requirementable_id);
                        // if ( filteredExperiences.length === 0 ) {
                        //     const additionalExperiences = this.applicantProfile.experiences;
                        //     // const additionalExperiences = this.applicantProfile.experiences.filter(exp => exp[domain.table] !== req.requirementable_id);
                        //     const yearArray = additionalExperiences.map(e => {
                        //         return e.end_year
                        //         ? e.end_year - e.start_year
                        //         : new Date().getFullYear() - e.start_year;
                        //     });
                        //     const applicantYears = yearArray.length > 0 ? yearArray.reduce((a, b) => a + b) : 0;
                        //     const objectName = domain.table.split('_id')[0];
                        //     return {
                        //         requirementable_id: additionalExperiences[0][domain.table],
                        //         requirements_id: null,
                        //         experiences_id: additionalExperiences.map(e => e.id),
                        //         applicantYears: applicantYears,
                        //         match: false,
                        //         name: additionalExperiences[0][objectName].name,
                        //         name_en: additionalExperiences[0].name_en ?? null,
                        //     }
                        // }
                        const experiences_id = filteredExperiences.map(e => e.id);
                        const yearArray = filteredExperiences.map(e => {
                            return e.end_year
                            ? e.end_year - e.start_year
                            : new Date().getFullYear() - e.start_year;
                        });
                        const applicantYears = yearArray.length > 0 ? yearArray.reduce((a, b) => a + b) : 0;

						return {
							requirementable_id: req.requirementable_id,
							requirements_id: req.id,
							experiences_id: experiences_id,
							jobYears: req.years,
							applicantYears: applicantYears,
							match: applicantYears >= req.years,
							name: req.name,
							name_en: req.name_en
						}
					}
				})

				return {
					...domain,
					experiences
				}
			})
		},
		filteredRequirements() {
			return this.newAggregation.filter(req => {
				return req.experiences && req.experiences.length > 0
			})
		}
	},
	data() {
		return {
			showDetailedExperience: false,
			blankAggregatedExperiences: [
				{
					name: 'industries',
					table: 'industry_id',
					experiences: {}
				},
				{
					name: 'occupationalFields',
					table: 'occupational_field_id',
					experiences: {}
				},
				{
					name: 'jobPositions',
					table: 'job_position_id',
					experiences: {}
				},
				{
					name: 'companySizes',
					table: 'company_size_id',
					experiences: {}
				}
			],
			aggregatedExperiences: [
				{
					name: 'industries',
					table: 'industry_id',
					experiences: [
						{
							requirementable_id: 6,
							requirements_id: 1041,
							experiences_id: [],
							match: false,
							jobYears: 1,
							applicantYears: 0,
							name: 'Consulting',
							name_en: 'Consulting'
						},
						{
							requirementable_id: 28,
							requirements_id: 1040,
							experiences_id: [170, 171, 172],
							match: true,
							jobYears: 4,
							applicantYears: 6,
							name: 'Software',
							name_en: 'Software'
						},
						{
							requirementable_id: 1,
							requirements_id: null,
							experiences_id: [171],
							match: false,
							jobYears: 0,
							applicantYears: 1,
							name: 'Education & Training',
							name_en: 'Education & Training'
						}
					]
				},
				{
					name: 'occupationalFields',
					table: 'occupational_field_id',
					experiences: [
						{
							requirementable_id: 4,
							requirements_id: 1042,
							experiences_id: [170, 171, 172],
							match: true,
							jobYears: 4,
							applicantYears: 6,
							name: 'IT',
							name_en: 'IT'
						},
						{
							requirementable_id: 8,
							requirements_id: 1043,
							experiences_id: [],
							match: false,
							jobYears: 4,
							applicantYears: 0,
							name: 'Marketing',
							name_en: 'Marketing'
						}
					]
				},
				{
					name: 'jobPositions',
					table: 'job_position_id',
					experiences: [
						{
							requirementable_id: 1,
							requirements_id: 1044,
							experiences_id: [172],
							match: true,
							jobYears: 4,
							applicantYears: 4,
							name: 'employee',
							name_en: null
						}
					]
				},
				{
					name: 'companySizes',
					table: 'company_size_id',
					experiences: [
						{
							requirementable_id: 4,
							requirements_id: 1038,
							experiences_id: [170, 172],
							match: true,
							jobYears: 1,
							applicantYears: 4,
							name: 'startup',
							name_en: null
						},
						{
							requirementable_id: 5,
							requirements_id: 1039,
							experiences_id: [],
							match: false,
							jobYears: 1,
							applicantYears: 0,
							name: 'ngo',
							name_en: null
						}
					]
				}
			]
		}
	},
	methods: {
		newGetRequirementMatch(requirement_type, id) {
			if (!requirement_type) return ''
			if (this.newAggregation) {
				const match = this.newAggregation.find(
					e => e.name === requirement_type
				)
				if (!match || !match.experiences) return ''
				const matchedExperience = match.experiences.find(
					e => e.requirementable_id === id
				)
				if (!matchedExperience) return ''
				if (matchedExperience.jobYears < 1) return 'bonus'
				return matchedExperience.match ? 'matched' : 'not-matched'
			}
		},
		getRequirementMatch(requirement_id) {
			if (!requirement_id) return ''
			if (this.match) {
				return this.match.details.experiences[requirement_id]
					? 'matched'
					: 'not-matched'
			}
		},
		getJobExperience(type) {
			const requirement = this.jobOffer.requirements.filter(
				r => r.requirementable_type === type
			)
			return requirement && requirement.length ? requirement : null
		}
	}
}
</script>
